import {
	afterNextRender,
	booleanAttribute,
	ChangeDetectionStrategy,
	Component,
	computed,
	DestroyRef,
	ElementRef,
	EnvironmentProviders,
	inject,
	Injector,
	INJECTOR,
	input,
	makeEnvironmentProviders,
	provideAppInitializer,
	resource,
	signal,
	ViewEncapsulation,
} from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { CommonColors } from '@inscomponents/core';
import { IconName } from '@skylab-assets/icons';
import { ICON_CONFIG, IconRegistryService } from './icon-registry.service';

export {
	ICON_CONFIG,
	provideIconConfig,
	type IconConfig
} from './icon-registry.service';

const componentHosts = new WeakSet<HTMLElement>();

type ElementWithCallbacks = new () => HTMLElement & {
	connectedCallback(): void;
	disconnectedCallback(): void;
};

export function registerIconCustomElement(injector: Injector): void {
	if (typeof customElements !== 'undefined' && !customElements.get('skl-icon')) {
		const NgIconElement = createCustomElement(IconComponent, { injector }) as unknown as ElementWithCallbacks;

		class SklIconElement extends NgIconElement {
			#ownsComponent = false;

			override connectedCallback(): void {
				if (this.#ownsComponent) {
					super.connectedCallback();
					return;
				}
				queueMicrotask(() => {
					if (this.#ownsComponent || !this.isConnected || componentHosts.has(this)) return;
					this.#ownsComponent = true;
					super.connectedCallback();
				});
			}

			override disconnectedCallback(): void {
				if (this.#ownsComponent) super.disconnectedCallback();
			}
		}

		customElements.define('skl-icon', SklIconElement);
	}
}

export function provideIconElements(): EnvironmentProviders {
	return makeEnvironmentProviders([
		provideAppInitializer(() => {
			const injector = inject(INJECTOR);
			registerIconCustomElement(injector);
		}),
	]);
}

export type IconShape = 'none' | 'circle' | 'rectangle';

export type IconUnit = 'px' | 'rem' | 'em' | 'vw' | 'vh';

export type IconPresetSize = 'normal' | 'small' | 'medium' | 'large';

export type IconSize =
	| `${number}${IconUnit}`
	| `${number}`
	| IconPresetSize;

const LAZY_LOAD_ROOT_MARGIN = '50px';

const PRESET_SIZES = {
	small: '0.75rem',
	normal: '1rem',
	medium: '1.5rem',
	large: '2rem',
} as const satisfies Record<IconPresetSize, string>;

@Component({
	selector: 'skl-icon',
	template: `
		<span
			part="icon"
			class="icon-inner"
			[innerHTML]="svgContent()"
		>
		</span>`,
	styles: `
		:host {
			--_icon-shape-padding: .1875rem;
			--_icon-size: var(--icon-size, 1rem);

			display: inline-grid;
			place-items: center;
			flex-shrink: 0;
			fill: var(--icon-color, currentColor);
			width: var(--_icon-size);
			height: var(--_icon-size);
		}
		.icon-inner {
			contain: strict;
			display: flex;
			height: 100%;
			width: 100%;
		}
		svg {
			display: block;
			height: 100%;
			width: 100%;
		}

		:host(.circle) {
			background-color: var(--icon-color, currentColor);
			border-radius: 50%;
			fill: var(--icon-inner-color, var(--text-inverse-color));
		}
		:host(.rectangle) {
			background-color: var(--icon-color, currentColor);
			border-radius: var(--border-radius-sm, 0.25rem);
			fill: var(--icon-inner-color, var(--text-inverse-color));
		}

		:host(.circle) .icon-inner,
		:host(.rectangle) .icon-inner {
			width: calc(100% - 2 * var(--_icon-shape-padding));
			height: calc(100% - 2 * var(--_icon-shape-padding));
			color: var(--icon-inner-color, var(--text-inverse-color));
		}
	`,
	host: {
		'[attr.aria-hidden]': 'ariaLabel() ? null : "true"',
		'[attr.aria-label]': 'ariaLabel() || null',
		'[attr.role]': 'ariaLabel() ? "img" : null',
		'[class]': 'hostClasses()',
		'[style.--_icon-size]': 'iconSize()',
		'[style.--icon-color]': 'color() ? "var(--" + color() + "-color)" : null',
		'[style.--icon-inner-color]': 'innerColor() ? "var(--" + innerColor() + "-color)" : null',
	},
	// Zwykły ShadowDom kopiuje do każdej ikony wszystkie style aplikacji, a SharedStylesHost
	// trzyma te kopie po zniszczeniu ikony razem z całym widokiem. Shadow DOM zostaje, bo ikony trafiają też do shadow root komponentów Stencil.
	// - Odrzucone opcje:
	// - ShadowDom, bo to on powoduje wyciek;
	// - Emulated, bo SVG z [innerHTML] nie dostaje atrybutu _ngcontent;
	// - None, czyli style globalne. Jest wykonalne, ale zdejmuje granicę dla zewnętrznych reguł. Można przerobić, po odejściu od web komponentów.
	// - Konsekwencje:
	// - API jest eksperymentalne, więc przy aktualizacji Angulara trzeba sprawdzić ten tryb - jeżeli się zmieni bedzie błąd kompilacji;
	encapsulation: ViewEncapsulation.ExperimentalIsolatedShadowDom,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IconComponent {
	readonly #elementRef = inject(ElementRef<HTMLElement>);
	readonly #iconRegistry = inject(IconRegistryService);
	readonly #destroyRef = inject(DestroyRef);
	readonly #config = inject(ICON_CONFIG, { optional: true });

	readonly ariaLabel = input<string | undefined>(undefined, { alias: 'aria-label' });
	readonly name = input<IconName>();
	readonly color = input<CommonColors>();
	readonly innerColor = input<CommonColors>();
	readonly size = input<IconSize>();
	readonly shape = input<IconShape>('none');
	readonly lazy = input(this.#config?.lazy ?? false, { transform: booleanAttribute });

	readonly #isIntersected = signal(false);
	readonly isVisible = computed(() => !this.lazy() || this.#isIntersected());

	readonly #svgResource = resource({
		params: () => {
			const name = this.name();
			return this.isVisible() && name ? name : undefined;
		},
		loader: ({ params: name }) => this.#iconRegistry.getIcon(name),
	});

	readonly svgContent = computed(() => this.#svgResource.value());

	readonly iconSize = computed(() => {
		const rawSize = this.size();
		if (!rawSize) return undefined;

		const resolved = PRESET_SIZES[rawSize as keyof typeof PRESET_SIZES] ?? rawSize;
		const match = resolved.match(/^(\d+(?:\.\d+)?)(px|rem|em|vw|vh)?$/);
		if (!match) {
			return resolved as IconSize;
		}
		const unit = (match[2] as IconUnit) || 'px';
		return `${parseFloat(match[1])}${unit}` satisfies IconSize;
	});

	readonly hostClasses = computed(() => {
		const classes: string[] = [];
		const color = this.color();
		if (color) classes.push(color);

		const innerColor = this.innerColor();
		if (innerColor) classes.push(`${innerColor}--inner`);

		const shape = this.shape();
		if (shape && shape !== 'none') classes.push(shape);

		return classes.join(' ');
	});

	constructor() {
		componentHosts.add(this.#elementRef.nativeElement);
		afterNextRender({
			read: () => this.#setupLazyObserver(),
		});
	}

	#setupLazyObserver(): void {
		if (!this.lazy() || this.#isIntersected()) return;

		if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
			this.#isIntersected.set(true);
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) {
					observer.disconnect();
					this.#isIntersected.set(true);
				}
			},
			{ rootMargin: LAZY_LOAD_ROOT_MARGIN },
		);

		observer.observe(this.#elementRef.nativeElement);
		this.#destroyRef.onDestroy(() => observer.disconnect());
	}
}
